/**
 * Copyright (c) Qwoach LLC 2026. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */
import BaseObject from "@shared/BaseObject.js";
import {isStickyNoteNodeData} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramStickyNoteHelper";
import {
	getLegendInteractionState,
	LEGEND_HOVER_KIND,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendHoverHelper";

const GROUP_STROKE_WIDTH = 2
const GROUP_DRAFT_LINE_DASH = [8, 6]
const GROUP_FOREIGN_EXCLUSION_PADDING = 8
const GROUP_NATIVE_EXPANSION_PADDING = 8
const GROUP_LAYER_VIEWPORT_PADDING = GROUP_FOREIGN_EXCLUSION_PADDING + GROUP_NATIVE_EXPANSION_PADDING + GROUP_STROKE_WIDTH * 4
const GROUP_DIM_OPACITY_MULTIPLIER = 0.35

export default class GenogramNodeGroupConvexNotchHullRenderer extends BaseObject {

	constructor({
		cy,
		getGroups,
		getGroupStyle,
	}) {
		super()
		this.cy = cy
		this.getGroups = getGroups || (() => [])
		this.getGroupStyle = getGroupStyle || (() => ({}))
		this.layer = null
		this.animationFrameId = null
		this._listeners = {
			nodeGeometryChanged: this.requestUpdate.bind(this),
			nodeResized: this.requestUpdate.bind(this),
			dataChanged: this.requestUpdate.bind(this),
			nodeClassChanged: this.requestUpdate.bind(this),
		}
		this.ensureLayer()
		this.addListeners()
	}

	addListeners() {
		this.cy.on("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.on("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.on("cyeditor.data", this._listeners.dataChanged)
		this.cy.on("class", "node", this._listeners.nodeClassChanged)
	}

	ensureLayer() {
		if(this.layer || !this.cy.layers){
			return
		}
		this.layer = this.cy.layers().nodeLayer.insertBefore("canvas")
		this.layer.callback(ctx => {
			this.renderIntoContext(ctx)
		})
		this.applyLayerViewportPadding()
	}

	applyLayerViewportPadding() {
		if(!this.layer?.node || this.layer.__qwViewportPaddingApplied){
			return
		}

		let viewportPadding = GROUP_LAYER_VIEWPORT_PADDING
		let layer = this.layer
		let pixelRatio = layer.pixelRatio || window.devicePixelRatio || 1
		layer.__qwViewportPaddingApplied = true

		layer.resize = (width, height) => {
			let paddedWidth = width + viewportPadding * 2
			let paddedHeight = height + viewportPadding * 2
			layer.node.style.left = `${-viewportPadding}px`
			layer.node.style.top = `${-viewportPadding}px`
			layer.node.style.width = `${paddedWidth}px`
			layer.node.style.height = `${paddedHeight}px`
			layer.node.width = paddedWidth * pixelRatio
			layer.node.height = paddedHeight * pixelRatio
			layer.update()
		}

		layer.setViewport = (tx, ty, zoom) => {
			layer.transform.tx = tx + viewportPadding
			layer.transform.ty = ty + viewportPadding
			layer.transform.zoom = zoom
			layer.update()
		}

		layer.resize(this.cy.width(), this.cy.height())
		let pan = this.cy.pan()
		layer.setViewport(pan.x, pan.y, this.cy.zoom())
	}

	update() {
		this.layer?.update()
	}

	requestUpdate() {
		if(this.animationFrameId){
			return
		}
		if(typeof requestAnimationFrame !== "function"){
			this.update()
			return
		}
		this.animationFrameId = requestAnimationFrame(() => {
			this.animationFrameId = null
			this.update()
		})
	}

	destroy() {
		this.cy.off("position add remove", "node", this._listeners.nodeGeometryChanged)
		this.cy.off("cyeditor.noderesize-resizing cyeditor.noderesize-resized", this._listeners.nodeResized)
		this.cy.off("cyeditor.data", this._listeners.dataChanged)
		this.cy.off("class", "node", this._listeners.nodeClassChanged)
		if(this.animationFrameId && typeof cancelAnimationFrame === "function"){
			cancelAnimationFrame(this.animationFrameId)
			this.animationFrameId = null
		}
		this.layer?.remove()
		this.layer = null
	}

	renderIntoContext(ctx) {
		let groups = this.getGroups()
		for(let group of groups){
			let outlinePoints = this.getGroupOutline(group)
			if(outlinePoints.length < 3){
				continue
			}
			let style = this.getEffectiveGroupStyle(group)
			this.drawGroup(ctx, outlinePoints, style)
		}
	}

	getEffectiveGroupStyle(group) {
		let style = this.getGroupStyle(group.color)
		if(group?.isDraft){
			style = {
				...style,
				lineDash: GROUP_DRAFT_LINE_DASH,
			}
		}
		if(!this.isGroupDimmed(group)){
			return style
		}

		return {
			...style,
			fill: this.multiplyRgbaAlpha(style.fill, GROUP_DIM_OPACITY_MULTIPLIER),
			stroke: this.multiplyRgbaAlpha(style.stroke, GROUP_DIM_OPACITY_MULTIPLIER),
		}
	}

	getGroupOutline(group) {
		let nativeNodes = this.getGroupNodes(group)
		if(nativeNodes.length === 1){
			return this.getSingleNodeOutline(nativeNodes[0])
		}
		let nativePoints = nativeNodes.map(node => this.getNodeCenter(node))
		if(nativePoints.length === 0){
			return []
		}
		let nativeHullNodes = this.buildConvexHull(nativeNodes)
		let outlinePoints = this.ensureClockwisePoints(this.getExpandedHullPoints(nativeHullNodes))
		if(outlinePoints.length < 3){
			return outlinePoints
		}
		let foreignCircles = this.getForeignNodeCircles(group)
		for(let foreignCircle of foreignCircles){
			if(!this.doesPolygonOverlapCircle(foreignCircle, outlinePoints)){
				continue
			}
			let nearestBoundaryData = this.getNearestBoundaryData(foreignCircle.center, outlinePoints)
			if(!nearestBoundaryData){
				continue
			}
			let segmentMeta = this.getSegmentMeta(outlinePoints, nearestBoundaryData.segmentIndex)
			let inwardOffset = foreignCircle.radius + GROUP_FOREIGN_EXCLUSION_PADDING
			let tangentDirection = segmentMeta.direction
			let shoulderOffset = foreignCircle.radius * 0.75
			let exclusionPoint1 = {
				x: foreignCircle.center.x - tangentDirection.x * shoulderOffset + segmentMeta.inwardNormal.x * inwardOffset,
				y: foreignCircle.center.y - tangentDirection.y * shoulderOffset + segmentMeta.inwardNormal.y * inwardOffset,
			}
			let exclusionPoint2 = {
				x: foreignCircle.center.x + tangentDirection.x * shoulderOffset + segmentMeta.inwardNormal.x * inwardOffset,
				y: foreignCircle.center.y + tangentDirection.y * shoulderOffset + segmentMeta.inwardNormal.y * inwardOffset,
			}
			outlinePoints = this.insertPointsAfterSegment(outlinePoints, nearestBoundaryData.segmentIndex, [
				exclusionPoint1,
				exclusionPoint2,
			])
		}
		return this.removeAdjacentDuplicatePoints(outlinePoints)
	}

	getSingleNodeOutline(node) {
		if(!node){
			return []
		}
		let boundingBox = node.boundingBox({
			includeLabels: false,
			includeOverlays: false,
		})
		let padding = GROUP_NATIVE_EXPANSION_PADDING + GROUP_STROKE_WIDTH
		return [
			{x: boundingBox.x1 - padding, y: boundingBox.y1 - padding},
			{x: boundingBox.x2 + padding, y: boundingBox.y1 - padding},
			{x: boundingBox.x2 + padding, y: boundingBox.y2 + padding},
			{x: boundingBox.x1 - padding, y: boundingBox.y2 + padding},
		]
	}

	getGroupNodes(group) {
		let nodeIds = group?.nodeIds || []
		return this.cy.collection(nodeIds.map(nodeId => this.cy.getElementById(nodeId)))
			.filter(node => this.isRenderableNode(node))
	}

	isGroupDimmed(group) {
		let groupNodes = this.getGroupNodes(group)
		if(groupNodes.length === 0){
			return false
		}

		let legendInteractionState = getLegendInteractionState(this.cy)
		if(!legendInteractionState.activeItems.length){
			return false
		}

		return !this.isGroupMatchedByLegendInteraction(group, legendInteractionState.activeItems)
	}

	isGroupMatchedByLegendInteraction(group, activeItems = []) {
		let groupNodeIds = group?.nodeIds || []
		if(groupNodeIds.length === 0){
			return false
		}

		return activeItems.some((item) => this.isMatchingGroupHoverItem(item, groupNodeIds))
	}

	isMatchingGroupHoverItem(item, groupNodeIds = []) {
		if(item?.kind !== LEGEND_HOVER_KIND.GROUP){
			return false
		}

		let hoverNodeIds = item.nodeIds || []
		if(hoverNodeIds.length !== groupNodeIds.length){
			return false
		}

		let hoverNodeIdSet = new Set(hoverNodeIds)
		return groupNodeIds.every((nodeId) => hoverNodeIdSet.has(nodeId))
	}

	getForeignNodeCircles(group) {
		let excludedNodeIds = new Set(group?.nodeIds || [])
		return this.cy.nodes()
			.filter(node => this.isForeignObstacleNode(node, excludedNodeIds))
			.map(node => ({
				center: this.getNodeCenter(node),
				radius: this.getNodeRadius(node),
			}))
	}

	isForeignObstacleNode(node, excludedNodeIds = new Set()) {
		let isObstacleNode = this.isRenderableNode(node) && !excludedNodeIds.has(node.id())
		if(!isObstacleNode){
			return false
		}
		return !isStickyNoteNodeData(node.data())
	}

	isRenderableNode(node) {
		return node.isNode() &&
			!node.data("isAuxNode") &&
			!node.data("tempElement") &&
			!node.hasClass("eh-handle") &&
			!node.hasClass("eh-ghost-node")
	}

	getNodeCenter(node) {
		let position = node.position()
		return {
			x: position.x,
			y: position.y,
		}
	}

	getNodeRadius(node) {
		let boundingBox = node.boundingBox({
			includeLabels: false,
			includeOverlays: false,
		})
		return Math.max(boundingBox.w, boundingBox.h) / 2
	}

	buildConvexHull(items = []) {
		let sortedItems = items.slice().sort((item1, item2) => {
			let point1 = this.getPointLikePosition(item1)
			let point2 = this.getPointLikePosition(item2)
			if(point1.x !== point2.x){
				return point1.x - point2.x
			}
			return point1.y - point2.y
		})
		if(sortedItems.length <= 2){
			return sortedItems
		}
		let lower = []
		for(let item of sortedItems){
			while(lower.length >= 2 && this.cross(lower[lower.length - 2], lower[lower.length - 1], item) <= 0){
				lower.pop()
			}
			lower.push(item)
		}
		let upper = []
		for(let i = sortedItems.length - 1; i >= 0; i--){
			let item = sortedItems[i]
			while(upper.length >= 2 && this.cross(upper[upper.length - 2], upper[upper.length - 1], item) <= 0){
				upper.pop()
			}
			upper.push(item)
		}
		lower.pop()
		upper.pop()
		return lower.concat(upper)
	}

	cross(point1, point2, point3) {
		point1 = this.getPointLikePosition(point1)
		point2 = this.getPointLikePosition(point2)
		point3 = this.getPointLikePosition(point3)
		return (point2.x - point1.x) * (point3.y - point1.y) -
			(point2.y - point1.y) * (point3.x - point1.x)
	}

	getPointLikePosition(item) {
		if(typeof item.position === "function"){
			return item.position()
		}
		return item
	}

	getExpandedHullPoints(hullNodes = []) {
		if(hullNodes.length === 0){
			return []
		}
		let hullCenters = hullNodes.map(node => this.getPointLikePosition(node))
		let centroid = this.getCentroid(hullCenters)
		return hullNodes.map(node => {
			let center = this.getPointLikePosition(node)
			let radius = this.getNodeRadius(node) + GROUP_NATIVE_EXPANSION_PADDING
			let outwardDirection = this.normalizeVector({
				x: center.x - centroid.x,
				y: center.y - centroid.y,
			}, {x: 0, y: 0})
			let tangentDirection = {
				x: -outwardDirection.y,
				y: outwardDirection.x,
			}
			let shoulderOffset = radius * 0.75
			return [
				{
					x: center.x - tangentDirection.x * shoulderOffset + outwardDirection.x * radius,
					y: center.y - tangentDirection.y * shoulderOffset + outwardDirection.y * radius,
				},
				{
					x: center.x + tangentDirection.x * shoulderOffset + outwardDirection.x * radius,
					y: center.y + tangentDirection.y * shoulderOffset + outwardDirection.y * radius,
				},
			]
		}).flat()
	}

	getSegmentMeta(points = [], segmentIndex) {
		let point1 = points[segmentIndex]
		let point2 = points[(segmentIndex + 1) % points.length]
		let direction = this.normalizeVector({
			x: point2.x - point1.x,
			y: point2.y - point1.y,
		}, {x: 1, y: 0})
		return {
			point1,
			point2,
			direction,
			inwardNormal: {
				x: direction.y,
				y: -direction.x,
			},
			outwardNormal: {
				x: -direction.y,
				y: direction.x,
			},
		}
	}

	getNearestBoundaryData(point, polygonPoints = []) {
		if(polygonPoints.length < 2){
			return null
		}
		let nearestBoundaryData = null
		let minimumDistanceSq = Number.POSITIVE_INFINITY
		for(let i = 0; i < polygonPoints.length; i++){
			let point1 = polygonPoints[i]
			let point2 = polygonPoints[(i + 1) % polygonPoints.length]
			let closestPointData = this.getClosestPointDataOnSegment(point, point1, point2)
			let distanceSq = this.getPointDistanceSq(point, closestPointData.point)
			if(distanceSq >= minimumDistanceSq){
				continue
			}
			minimumDistanceSq = distanceSq
			nearestBoundaryData = {
				point: closestPointData.point,
				segmentIndex: i,
				segmentProjection: closestPointData.projection,
				distanceSq,
			}
		}
		return nearestBoundaryData
	}

	getClosestPointDataOnSegment(point, segmentPoint1, segmentPoint2) {
		let segmentVector = {
			x: segmentPoint2.x - segmentPoint1.x,
			y: segmentPoint2.y - segmentPoint1.y,
		}
		let segmentLengthSq = segmentVector.x * segmentVector.x + segmentVector.y * segmentVector.y
		if(segmentLengthSq === 0){
			return {
				projection: 0,
				point: {x: segmentPoint1.x, y: segmentPoint1.y},
			}
		}
		let projection = ((point.x - segmentPoint1.x) * segmentVector.x + (point.y - segmentPoint1.y) * segmentVector.y) / segmentLengthSq
		let clampedProjection = Math.max(0, Math.min(1, projection))
		return {
			projection: clampedProjection,
			point: {
				x: segmentPoint1.x + segmentVector.x * clampedProjection,
				y: segmentPoint1.y + segmentVector.y * clampedProjection,
			},
		}
	}

	insertPointsAfterSegment(points = [], segmentIndex, insertedPoints = []) {
		let nextPoints = points.slice()
		let normalizedInsertedPoints = insertedPoints.map(point => ({x: point.x, y: point.y}))
		nextPoints.splice(segmentIndex + 1, 0, ...normalizedInsertedPoints)
		return nextPoints
	}

	isPointInsidePolygon(point, polygonPoints = []) {
		let isInside = false
		for(let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++){
			let point1 = polygonPoints[i]
			let point2 = polygonPoints[j]
			let doesIntersect = ((point1.y > point.y) !== (point2.y > point.y)) &&
				(point.x < (point2.x - point1.x) * (point.y - point1.y) / ((point2.y - point1.y) || 1e-9) + point1.x)
			if(doesIntersect){
				isInside = !isInside
			}
		}
		return isInside
	}

	doesPolygonOverlapCircle(circle, polygonPoints = []) {
		if(this.isPointInsidePolygon(circle.center, polygonPoints)){
			return true
		}
		return this.getClosedPolylineDistanceSq(circle.center, polygonPoints) < circle.radius * circle.radius
	}

	getClosedPolylineDistanceSq(point, polygonPoints = []) {
		let minimumDistanceSq = Number.POSITIVE_INFINITY
		for(let i = 0; i < polygonPoints.length; i++){
			let point1 = polygonPoints[i]
			let point2 = polygonPoints[(i + 1) % polygonPoints.length]
			let closestPointData = this.getClosestPointDataOnSegment(point, point1, point2)
			let distanceSq = this.getPointDistanceSq(point, closestPointData.point)
			if(distanceSq < minimumDistanceSq){
				minimumDistanceSq = distanceSq
			}
		}
		return minimumDistanceSq
	}

	drawGroup(ctx, points = [], style = {}) {
		ctx.save()
		this.tracePolygonOutline(ctx, points)
		ctx.fillStyle = style.fill
		ctx.strokeStyle = style.stroke
		ctx.lineWidth = Number(style.strokeWidth) || GROUP_STROKE_WIDTH
		ctx.lineJoin = "round"
		ctx.lineCap = "round"
		ctx.setLineDash(style.lineDash || [])
		ctx.fill()
		ctx.stroke()
		ctx.restore()
	}

	multiplyRgbaAlpha(colorValue, multiplier = 1) {
		if(typeof colorValue !== "string"){
			return colorValue
		}

		let rgbaMatch = colorValue.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)$/i)
		if(!rgbaMatch){
			return colorValue
		}

		let [, red, green, blue, alpha] = rgbaMatch
		let nextAlpha = Math.max(0, Math.min(1, Number(alpha) * multiplier))
		return `rgba(${red}, ${green}, ${blue}, ${nextAlpha})`
	}

	tracePolygonOutline(ctx, points = []) {
		ctx.beginPath()
		if(points.length === 0){
			return
		}
		if(points.length < 3){
			ctx.moveTo(points[0].x, points[0].y)
			for(let i = 1; i < points.length; i++){
				ctx.lineTo(points[i].x, points[i].y)
			}
			ctx.closePath()
			return
		}
		let startMidpoint = this.getObjectMidpoint(points[0], points[1])
		ctx.moveTo(startMidpoint.x, startMidpoint.y)
		for(let i = 1; i <= points.length; i++){
			let currentPoint = points[i % points.length]
			let nextPoint = points[(i + 1) % points.length]
			let nextMidpoint = this.getObjectMidpoint(currentPoint, nextPoint)
			ctx.quadraticCurveTo(currentPoint.x, currentPoint.y, nextMidpoint.x, nextMidpoint.y)
		}
		ctx.closePath()
	}

	removeAdjacentDuplicatePoints(points = []) {
		if(points.length <= 1){
			return points
		}
		let filteredPoints = [points[0]]
		for(let i = 1; i < points.length; i++){
			let previousPoint = filteredPoints[filteredPoints.length - 1]
			let currentPoint = points[i]
			if(this.getPointDistanceSq(previousPoint, currentPoint) <= 1){
				continue
			}
			filteredPoints.push(currentPoint)
		}
		if(filteredPoints.length > 1 && this.getPointDistanceSq(filteredPoints[0], filteredPoints[filteredPoints.length - 1]) <= 1){
			filteredPoints.pop()
		}
		return filteredPoints
	}

	getPointDistanceSq(point1, point2) {
		let dx = point1.x - point2.x
		let dy = point1.y - point2.y
		return dx * dx + dy * dy
	}

	getObjectMidpoint(point1, point2) {
		return {
			x: (point1.x + point2.x) / 2,
			y: (point1.y + point2.y) / 2,
		}
	}

	getCentroid(points = []) {
		let sumX = 0
		let sumY = 0
		for(let point of points){
			sumX += point.x
			sumY += point.y
		}
		return {
			x: sumX / points.length,
			y: sumY / points.length,
		}
	}

	getSignedPolygonArea(points = []) {
		let doubleArea = 0
		for(let i = 0; i < points.length; i++){
			let point1 = points[i]
			let point2 = points[(i + 1) % points.length]
			doubleArea += point1.x * point2.y - point2.x * point1.y
		}
		return doubleArea / 2
	}

	ensureClockwisePoints(points = []) {
		if(this.getSignedPolygonArea(points) <= 0){
			return points
		}
		return points.slice().reverse()
	}

	normalizeVector(vector, fallback = {x: 0, y: 0}) {
		let lengthSq = vector.x * vector.x + vector.y * vector.y
		if(lengthSq === 0){
			return fallback
		}
		let length = Math.sqrt(lengthSq)
		return {
			x: vector.x / length,
			y: vector.y / length,
		}
	}

}
